import { Component, Input, OnInit, OnDestroy, EventEmitter, Output, inject } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  first,
  firstValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { WorkerAgreementDetailsDto } from '../../../models/dtos/worker-agreement-details-dto';
import { EmploymentAgreementDetailsDto } from 'src/app/models/dtos/employment-agreement-details-dto';
import { AnnexChangedDataStepModel } from 'src/app/models/dtos/annexes/annex-changed-data-step-model';
import { TreeNode } from 'src/app/shared/models/tree-node';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { AnnexService } from 'src/app/data/annex.service';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { TreeViewerComponent, TreeViewerComponentData } from 'src/app/shared/components/tree-viewer/tree-viewer.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Messages } from 'src/app/common/enums';
import { WorkerAgreementForAnnexDto } from 'src/app/models/dtos/worker-agreements/worker-agreement-for-annex-dto';
import { WageTypeDto } from 'src/app/models/dtos/wage-type-dto';
import { MatTableDataSource } from '@angular/material/table';
import { WageDescription } from 'src/app/models/enums/WageDescription';
import { EmploymentType } from 'src/app/models/enums/employment-type-enum';
import { WageDto } from 'src/app/models/dtos/employment-agreement-dto';
import { CurrencyEnum } from 'src/app/models/enums/currency-enum';
import { WageValidator } from 'src/app/shared/validators/wage.validator';
import { SupervisorDto } from 'src/app/models/supervisor.dto';
import { SupervisorsService } from 'src/app/data/supervisors.service';
import { autocompleteValidator } from 'src/app/shared/validators/autocomplete.validator';
import { trigger, transition, style, animate } from '@angular/animations';
import { DictionaryItem } from 'src/app/models/dictionary-item';

type ChangedDataForm = FormGroup<{
  hasChangesJobTitle: FormControl<boolean>;
  organizationalUnit: FormControl<TreeNode | DictionaryItem>;
  jobTitle: FormControl<DictionaryItem>;
  hasChangesWages: FormControl<boolean>;
  wages: FormArray<FormGroup>;
  hasChangesTemporaryWorkplace: FormControl<boolean>;
  temporaryWorkplace: FormControl<DictionaryItem | string>;
  hasChangesPotentialSupervisor: FormControl<boolean>;
  potentialSupervisor: FormControl<SupervisorDto | string>;
  hasChangesWorkingTimeGroup: FormControl<boolean>;
  workingTimeGroupId: FormControl<number>;
}>;

interface SectionLoadingState {
  isLoading: boolean;
  showSuccess: boolean;
}

@Component({
  selector: 'app-changed-data-step',
  templateUrl: './changed-data-step.component.html',
  styleUrls: ['./changed-data-step.component.scss'],
  standalone: false,
  animations: [
    trigger('successAnimation', [
      transition(':enter', [style({ transform: 'scale(0)', opacity: 0 }), animate('300ms ease-out', style({ transform: 'scale(1)', opacity: 1 }))]),
      transition(':leave', [animate('200ms ease-in', style({ transform: 'scale(0)', opacity: 0 }))]),
    ]),
  ],
})
export class ChangedDataStepComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private dictionaryService = inject(DictionaryService);
  private annexService = inject(AnnexService);
  private dialog = inject(MatDialog);
  private supervisorsService = inject(SupervisorsService);

  @Input() agreement: WorkerAgreementDetailsDto | EmploymentAgreementDetailsDto;
  @Input() dataForAnnex: WorkerAgreementForAnnexDto;
  @Output() dataChange = new EventEmitter<{ data: AnnexChangedDataStepModel; sectionKey: string }>();

  loadingStates: { [key: string]: SectionLoadingState } = {
    temporaryWorkplace: { isLoading: false, showSuccess: false },
    wage: { isLoading: false, showSuccess: false },
    potentialSupervisor: { isLoading: false, showSuccess: false },
    jobTitle: { isLoading: false, showSuccess: false },
    workingTimeGroup: { isLoading: false, showSuccess: false },
  };

  listOfOrganizationalUnits$: Observable<TreeNode[]>;
  listOfJobTitles$: Observable<DictionaryItem[]> = of([]);
  selectedJobTitle$: Observable<DictionaryItem | null>;
  listOfTemporaryWorkplaces$: Observable<DictionaryItem[]> = of([]);
  listOfSupervisors$: Observable<SupervisorDto[]> = of([]);
  listOfWorkingTimeGroups$: Observable<DictionaryItem[]> = of([]);

  listOfWageTypes$: Observable<WageTypeDto[]>;
  listOfCurrencies$: Observable<DictionaryItem[]>;
  listOfWageDescriptions$: Observable<DictionaryItem[]>;
  hasAnyDescriptiveWageType$: Observable<boolean>;
  canAddWage$: Observable<boolean>;

  private organizationalUnitsSubject$ = new BehaviorSubject<number>(null);
  private destroy$ = new Subject<void>();
  protected readonly timeBetweenInput = 300;
  protected readonly minimumInputLetters = 2;

  changedDataForm!: ChangedDataForm;
  availableComponents: string[] = [];
  isJobTitleSectionEnabled = false;
  isWagesSectionEnabled = false;
  isTemporaryWorkplaceSectionEnabled = false;
  isSupervisorSectionEnabled = false;
  isWorkingTimeGroupSectionEnabled = false;
  isLoadingConfiguration = true;

  wagesDataSource = new MatTableDataSource<AbstractControl>();
  private listOfWageTypes: WageTypeDto[] = [];
  private readonly minimumWages = 1;
  private readonly maxWageDescriptionLength = 2000;
  private readonly minWageDescriptionLength = 2;
  private wageCounter = 0;
  private employmentTypeId: number = null;
  private currentJobTitle: DictionaryItem | null = null;
  private jobTitles: DictionaryItem[] = [];

  private readonly wageDescriptionsThatCannotOccurrSimultaneously: Map<WageDescription, WageDescription[]> = new Map([
    [WageDescription.Daily, [WageDescription.Daily]],
    [WageDescription.Night, [WageDescription.Night]],
  ]);

  get hasChangesJobTitle() {
    return this.changedDataForm.get('hasChangesJobTitle');
  }

  get organizationalUnit() {
    return this.changedDataForm.get('organizationalUnit');
  }

  get jobTitle() {
    return this.changedDataForm.get('jobTitle');
  }

  get hasChangesWages() {
    return this.changedDataForm.get('hasChangesWages');
  }

  get wages(): FormArray {
    return this.changedDataForm.get('wages') as FormArray;
  }

  get hasChangesTemporaryWorkplace() {
    return this.changedDataForm.get('hasChangesTemporaryWorkplace');
  }

  get temporaryWorkplace() {
    return this.changedDataForm.get('temporaryWorkplace');
  }

  get hasChangesPotentialSupervisor() {
    return this.changedDataForm.get('hasChangesPotentialSupervisor');
  }

  get potentialSupervisor() {
    return this.changedDataForm.get('potentialSupervisor');
  }

  get hasChangesWorkingTimeGroup() {
    return this.changedDataForm.get('hasChangesWorkingTimeGroup');
  }

  get workingTimeGroupId() {
    return this.changedDataForm.get('workingTimeGroupId');
  }

  get isFormValid(): boolean {
    return this.changedDataForm.valid;
  }

  get hasAnyChanges(): boolean {
    return (
      this.hasChangesTemporaryWorkplace.value ||
      this.hasChangesPotentialSupervisor.value ||
      this.hasChangesWages.value ||
      this.hasChangesJobTitle.value ||
      this.hasChangesWorkingTimeGroup.value
    );
  }

  async ngOnInit(): Promise<void> {
    this.employmentTypeId = this.agreement.EmploymentTypeId;
    this.initForm();
    await this.loadAnnexConfiguration();
    this.setupOrganizationalUnits();
    this.setupTemporaryWorkplaceObservable();
    this.setupWagesObservables();
    this.setupSupervisorObservable();
    this.setupWorkingTimeGroup();
    this.setupFormChangeEmitter();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadAnnexConfiguration(): Promise<void> {
    const config = await firstValueFrom(this.annexService.getAnnexComponentConfiguration(this.agreement.AgreementId));

    this.availableComponents = config.map((c) => c.Name);
    this.isJobTitleSectionEnabled = this.availableComponents.includes('JobTitle');
    this.isWagesSectionEnabled = this.availableComponents.includes('Wage');
    this.isTemporaryWorkplaceSectionEnabled = this.availableComponents.includes('TemporaryWorkplace');
    this.isSupervisorSectionEnabled = this.availableComponents.includes('Supervisor');
    this.isWorkingTimeGroupSectionEnabled = this.availableComponents.includes('WorkingTimeGroup');

    if (!this.isJobTitleSectionEnabled) {
      this.hasChangesJobTitle.disable();
    }

    if (!this.isTemporaryWorkplaceSectionEnabled) {
      this.hasChangesTemporaryWorkplace.disable();
    }

    if (!this.isWagesSectionEnabled) {
      this.hasChangesWages.disable();
    }

    if (!this.isSupervisorSectionEnabled) {
      this.hasChangesPotentialSupervisor.disable();
    }

    if (!this.isWorkingTimeGroupSectionEnabled) {
      this.hasChangesWorkingTimeGroup.disable();
    }
  }

  private initForm(): void {
    this.changedDataForm = this.fb.group({
      hasChangesJobTitle: this.fb.nonNullable.control(false),
      organizationalUnit: this.fb.control<TreeNode | DictionaryItem>({ value: null, disabled: true }),
      jobTitle: this.fb.control<DictionaryItem>({ value: null, disabled: true }),
      hasChangesWages: this.fb.nonNullable.control(false),
      wages: this.fb.array<FormGroup>([]),
      hasChangesTemporaryWorkplace: this.fb.nonNullable.control(false),
      temporaryWorkplace: this.fb.control<DictionaryItem | string>({ value: null, disabled: true }),
      hasChangesPotentialSupervisor: this.fb.nonNullable.control(false),
      potentialSupervisor: this.fb.control<SupervisorDto | string>({ value: null, disabled: true }),
      hasChangesWorkingTimeGroup: this.fb.nonNullable.control(false),
      workingTimeGroupId: this.fb.control<number>({ value: null, disabled: true }),
    });

    this.hasChangesJobTitle.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((checked) => {
      this.toggleJobTitleSection(checked);
    });
    this.hasChangesWages.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((checked) => this.toggleWagesSection(checked));
    this.hasChangesTemporaryWorkplace.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((checked) => this.toggleTemporaryWorkplaceSection(checked));
    this.hasChangesPotentialSupervisor.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((checked) => this.toggleSupervisorSection(checked));
    this.hasChangesWorkingTimeGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((checked) => this.toggleWorkingTimeGroupSection(checked));
  }

  private setupOrganizationalUnits(): void {
    this.listOfOrganizationalUnits$ = this.organizationalUnitsSubject$.asObservable().pipe(
      switchMap((companyId: number) => {
        if (!companyId) return of([]);
        return this.dictionaryService
          .getOrganizationalUnits(companyId, this.agreement.EmploymentTypeId)
          .pipe(map((arr) => arr.map((ou) => <any>{ ...ou, Checked: false, Expanded: false })));
      }),
      takeUntil(this.destroy$),
    );
  }

  private setupWagesObservables(): void {
    this.listOfCurrencies$ = this.dictionaryService.getCurrencies();
    this.listOfWageDescriptions$ = this.dictionaryService.getWageDescriptions();

    this.listOfWageTypes$ = this.dictionaryService
      .getWageTypesByParameters(this.dataForAnnex.AgreementTypeId, this.dataForAnnex.EmployerObjectId)
      .pipe(
        tap((wageTypes) => (this.listOfWageTypes = wageTypes)),
        shareReplay(1),
      );

    this.hasAnyDescriptiveWageType$ = this.listOfWageTypes$.pipe(map((wts) => wts.some((wt) => wt.IsDescriptiveType)));

    this.canAddWage$ = combineLatest([this.wages.valueChanges.pipe(startWith(this.wages.value)), this.listOfWageTypes$]).pipe(
      map(([wages, wageTypes]) => {
        if (!this.isEmploymentAgreementType()) {
          return false; // nie pozwalamy dodawać nowych stawek
        }

        if (this.isEmploymentAgreementType()) {
          return wages.length < 1;
        }
        return wages.length < wageTypes.length;
      }),
    );
  }

  private toggleWagesSection(isEnabled: boolean): void {
    if (isEnabled) {
      this.wages.setValidators([this.atLeastOneWageChangedValidator()]);

      if (!this.isEmploymentAgreementType()) {
        this.loadExistingWages();
      } else {
        this.addNewWage();
      }
    } else {
      this.wages.clearValidators();
      this.resetWages();
    }

    this.wages.updateValueAndValidity();
  }

  private loadExistingWages(): void {
    const currentWages = this.dataForAnnex.Wages || [];
    currentWages.forEach((wage) => {
      this.wages.push(this.createWageFormGroup(wage));
      this.wageCounter++;
    });
    this.wagesDataSource.data = this.wages.controls;
  }

  private atLeastOneWageChangedValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const wagesArray = control as FormArray;
      if (!wagesArray || wagesArray.length === 0) return { noWages: true };

      const currentWages = this.dataForAnnex.Wages || [];
      const newWages = wagesArray.getRawValue();

      const hasChanges = newWages.some((newWage: any) => {
        return !currentWages.some((currentWage: WageDto) => this.areWagesEqual(newWage, currentWage));
      });

      if (!hasChanges) {
        return { noWageChanges: true };
      }

      return null;
    };
  }

  private areWagesEqual(wage1: any, wage2: WageDto): boolean {
    if (wage1.value !== undefined && wage2.Value !== undefined) {
      return wage1.value === wage2.Value && wage1.currencyId === wage2.CurrencyId && wage1.wageTypeId === wage2.WageTypeId;
    }

    if (wage1.name !== undefined && wage2.Name !== undefined) {
      return wage1.name === wage2.Name && wage1.calculationDescription === wage2.CalculationDescription && wage1.wageTypeId === wage2.WageTypeId;
    }

    return false;
  }

  public async addNewWage(wage: Partial<WageDto> = {}): Promise<void> {
    const canAdd = await firstValueFrom(this.canAddWage$);
    if (!canAdd) return;

    this.wages.push(this.createWageFormGroup(wage));
    this.wagesDataSource.data = this.wages.controls;
    this.wageCounter++;
  }

  public deleteWage(index: number): void {
    this.wages.removeAt(index);
    this.wagesDataSource.data = this.wages.controls;
    this.wageCounter--;
  }

  public resetWages(): void {
    while (this.wages.length !== 0) {
      this.wages.removeAt(0);
    }
    this.wageCounter = 0;
    this.wagesDataSource.data = [];
  }

  public canDeleteWage(): boolean {
    if (!this.isEmploymentAgreementType()) {
      return false;
    }

    return this.wageCounter > this.minimumWages;
  }

  private createWageFormGroup(wage: Partial<WageDto> = {}): FormGroup {
    const wageForm = this.fb.group({
      value: new FormControl(
        wage.Value || null,
        [Validators.required],
        [
          (control: AbstractControl) => WageValidator.maxWage(this.listOfWageTypes$, control),
          (control: AbstractControl) => WageValidator.minWage(this.listOfWageTypes$, control),
        ],
      ),
      currencyId: new FormControl(wage.CurrencyId || CurrencyEnum.PLN, [Validators.required]),
      wageTypeId: new FormControl(wage.WageTypeId || null, [Validators.required]),
      wageDescriptionId: new FormControl(wage.WageDescriptionId || null),
      name: new FormControl(wage.Name || null, [
        Validators.required,
        Validators.minLength(this.minWageDescriptionLength),
        Validators.maxLength(this.maxWageDescriptionLength),
      ]),
      calculationDescription: new FormControl(wage.CalculationDescription || null, [
        Validators.required,
        Validators.minLength(this.minWageDescriptionLength),
        Validators.maxLength(this.maxWageDescriptionLength),
      ]),
    });

    if (!this.isEmploymentAgreementType() && wage.WageTypeId) {
      wageForm.get('wageTypeId').disable();
    }

    const wageValueControl = () => wageForm.get('value');

    combineLatest([wageForm.valueChanges, this.listOfWageTypes$])
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(([changedWage, wageTypes]) => {
        const wageType = wageTypes.find((wt) => wt.Id === changedWage.wageTypeId);
        if (!wageType?.IsDescriptiveType) {
          wageValueControl()?.updateValueAndValidity({ emitEvent: false });
        }
      });

    combineLatest([wageForm.get('wageTypeId').valueChanges.pipe(startWith(wageForm.get('wageTypeId').value)), this.listOfWageTypes$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([wageTypeId, wageTypes]) => {
        const wageType = wageTypes.find((wt) => wt.Id === wageTypeId);
        if (!wageType) return;

        if (wageType.IsDescriptiveType) {
          this.setDescriptiveWageTypeValidators(wageForm);
        } else {
          this.setValueWageTypeValidators(wageForm);
        }
      });

    return wageForm;
  }

  private setDescriptiveWageTypeValidators(wageForm: FormGroup): void {
    wageForm.get('value').reset();
    wageForm.get('currencyId').reset();
    wageForm.get('value').disable();
    wageForm.get('currencyId').disable();
    wageForm.get('name').enable();
    wageForm.get('calculationDescription').enable();
    wageForm.updateValueAndValidity({ emitEvent: false });
  }

  private setValueWageTypeValidators(wageForm: FormGroup): void {
    wageForm.get('value').enable();
    wageForm.get('currencyId').enable();
    wageForm.get('name').reset();
    wageForm.get('calculationDescription').reset();
    wageForm.get('name').disable();
    wageForm.get('calculationDescription').disable();
    wageForm.updateValueAndValidity({ emitEvent: false });
  }

  public isDescriptiveType(wageTypeId: number): boolean {
    return this.listOfWageTypes.find((wt) => wt.Id === wageTypeId)?.IsDescriptiveType || false;
  }

  public getWageTypeName(wageTypeId: number): string {
    return this.listOfWageTypes.find((wt) => wt.Id === wageTypeId)?.Name || '';
  }

  public filterWageDescriptions(wageDescriptions: DictionaryItem[], index: number): DictionaryItem[] {
    if (!wageDescriptions || !this.wages.value) return wageDescriptions;

    return wageDescriptions.filter((item: DictionaryItem) => {
      return this.wages.value.every((wage: any, wageIndex: number) => {
        if (wageIndex === index) return true;
        if (!this.wageDescriptionsThatCannotOccurrSimultaneously.has(wage.wageDescriptionId)) {
          return true;
        }
        return !this.wageDescriptionsThatCannotOccurrSimultaneously.get(wage.wageDescriptionId).some((excludedId) => excludedId === item.Id);
      });
    });
  }

  private toggleTemporaryWorkplaceSection(isEnabled: boolean): void {
    const temporaryWorkplaceControl = this.temporaryWorkplace;

    if (isEnabled) {
      temporaryWorkplaceControl.enable();
      temporaryWorkplaceControl.setValidators([Validators.required, this.temporaryWorkPlaceMustBeDifferentValidator()]);
    } else {
      temporaryWorkplaceControl.disable();
      temporaryWorkplaceControl.clearValidators();
      temporaryWorkplaceControl.setValue(null);
    }

    temporaryWorkplaceControl.updateValueAndValidity();
  }

  private toggleSupervisorSection(isEnabled: boolean): void {
    const potentialSupervisorControl = this.potentialSupervisor;

    if (isEnabled) {
      potentialSupervisorControl.enable();
      potentialSupervisorControl.setValidators([Validators.required, this.potentialSupervisorMustBeDifferentValidator(), autocompleteValidator]);
    } else {
      potentialSupervisorControl.disable();
      potentialSupervisorControl.clearValidators();
      potentialSupervisorControl.setValue(null);
    }

    potentialSupervisorControl.updateValueAndValidity();
  }

  private toggleWorkingTimeGroupSection(isEnabled: boolean): void {
    const workingTimeGroupControl = this.workingTimeGroupId;

    if (isEnabled) {
      workingTimeGroupControl.enable();
      workingTimeGroupControl.setValidators([Validators.required, this.workingTimeGroupMustBeDifferentValidator()]);
    } else {
      workingTimeGroupControl.disable();
      workingTimeGroupControl.clearValidators();
      workingTimeGroupControl.setValue(null);
    }

    workingTimeGroupControl.updateValueAndValidity();
  }

  private workingTimeGroupMustBeDifferentValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const selectedWorkingTimeGroupId = control.value;
      const currentWorkingTimeGroupId = this.dataForAnnex.WorkingTimeGroupId;

      if (selectedWorkingTimeGroupId === currentWorkingTimeGroupId) {
        return { sameAsCurrentWorkingTimeGroup: true };
      }

      return null;
    };
  }

  private toggleJobTitleSection(isEnabled: boolean): void {
    const orgUnitControl = this.organizationalUnit;
    const jobTitleControl = this.jobTitle;

    if (isEnabled) {
      orgUnitControl.enable();
      jobTitleControl.enable();

      orgUnitControl.setValidators([Validators.required]);
      jobTitleControl.setValidators([Validators.required, this.jobTitleMustBeDifferentValidator()]);

      this.organizationalUnitsSubject$.next(this.agreement.CompanyId);

      this.setupJobTitlesObservable();
      this.setupSelectedJobTitleObservable();
    } else {
      orgUnitControl.disable();
      jobTitleControl.disable();
      orgUnitControl.clearValidators();
      jobTitleControl.clearValidators();
      orgUnitControl.setValue(null);
      jobTitleControl.setValue(null);

      this.listOfJobTitles$ = of([]);
    }

    orgUnitControl.updateValueAndValidity();
    jobTitleControl.updateValueAndValidity();
  }

  private temporaryWorkPlaceMustBeDifferentValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const selectedWorkplace = control.value;
      let temporaryWorkplaceName: string | null = null;
      if (!selectedWorkplace) {
        temporaryWorkplaceName = null;
      } else {
        if (typeof selectedWorkplace === 'object') {
          temporaryWorkplaceName = selectedWorkplace.Name;
        } else {
          temporaryWorkplaceName = selectedWorkplace;
        }
      }

      const currentWorkplaceName = this.dataForAnnex.TemporaryWorkplaceName;
      if (temporaryWorkplaceName === currentWorkplaceName) {
        return { sameAsCurrentTemporaryWorkplace: true };
      }

      return null;
    };
  }

  private potentialSupervisorMustBeDifferentValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const selectedPotentialSupervisorId = control.value?.Id;
      const currentPotentialSupervisorId = this.dataForAnnex.PotentialSupervisorId;
      if (selectedPotentialSupervisorId === currentPotentialSupervisorId) {
        return { sameAsCurrentSupervisor: true };
      }

      return null;
    };
  }

  private jobTitleMustBeDifferentValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const selectedJobTitleId = control.value;
      const currentJobTitleId = this.dataForAnnex.JobTitleId;

      if (selectedJobTitleId === currentJobTitleId) {
        return { sameAsCurrentJobTitle: true };
      }

      const selectedName = this.jobTitles.find((jt) => jt.Id === selectedJobTitleId)?.Name;
      const currentName = this.dataForAnnex.JobTitleName;

      if (selectedName && currentName && selectedName === currentName) {
        return { sameAsCurrentJobTitle: true };
      }

      return null;
    };
  }

  private setupJobTitlesObservable(): void {
    this.listOfJobTitles$ = this.organizationalUnit.valueChanges.pipe(
      takeUntil(this.destroy$),
      startWith(this.organizationalUnit.value),
      distinctUntilChanged(),
      switchMap((value: TreeNode | DictionaryItem) => {
        this.jobTitle.setValue(null, { emitEvent: false });
        this.jobTitle.markAsTouched();
        this.jobTitle.updateValueAndValidity();

        if (!value || !value.Id) {
          return of([]);
        }
        return this.dictionaryService.getJobTitles(null, value.Id);
      }),
      tap((jobTitles) => (this.jobTitles = jobTitles)),
      shareReplay(1),
    );
  }

  private setupSupervisorObservable(): void {
    this.listOfSupervisors$ = this.potentialSupervisor.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(this.timeBetweenInput),
      distinctUntilChanged(),
      switchMap((value: string) => {
        if (value && value.length > this.minimumInputLetters) {
          return this.supervisorsService.getPotentialSupervisors(this.dataForAnnex.EmployerObjectId, value);
        } else {
          return of(null);
        }
      }),
    );
  }

  private setupTemporaryWorkplaceObservable(): void {
    this.listOfTemporaryWorkplaces$ = this.temporaryWorkplace.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(this.timeBetweenInput),
      distinctUntilChanged(),
      switchMap((value: string) => {
        if (value && value.length > this.minimumInputLetters) {
          return this.dictionaryService.getTemporaryWorkplaces(value);
        } else {
          return of(null);
        }
      }),
    );
  }

  private setupSelectedJobTitleObservable(): void {
    this.selectedJobTitle$ = combineLatest([
      this.jobTitle.valueChanges.pipe(startWith(this.jobTitle.value)),
      this.listOfJobTitles$.pipe(startWith([])),
    ]).pipe(
      map(([selectedId, jobTitles]) => {
        if (!selectedId || !jobTitles || jobTitles.length === 0) {
          return null;
        }
        return jobTitles.find((j) => j.Id === selectedId) || null;
      }),
      takeUntil(this.destroy$),
      shareReplay(1),
    );

    this.selectedJobTitle$.pipe(takeUntil(this.destroy$)).subscribe((selectedJobTitle) => {
      this.currentJobTitle = selectedJobTitle;
    });
  }

  private setupWorkingTimeGroup(): void {
    this.listOfWorkingTimeGroups$ = this.dictionaryService.getWorkingTimeGroups();
  }

  private async setupFormChangeEmitter(): Promise<void> {
    combineLatest([
      this.hasChangesTemporaryWorkplace.valueChanges,
      this.temporaryWorkplace.valueChanges.pipe(startWith(this.temporaryWorkplace.value)),
    ])
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.emitFormData('temporaryWorkplace');
      });

    combineLatest([
      this.hasChangesPotentialSupervisor.valueChanges,
      this.potentialSupervisor.valueChanges.pipe(startWith(this.potentialSupervisor.value)),
    ])
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.emitFormData('potentialSupervisor');
      });

    combineLatest([this.hasChangesWages.valueChanges, this.wages.valueChanges.pipe(startWith(this.wages.value))])
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.emitFormData('wage');
      });

    combineLatest([
      this.hasChangesJobTitle.valueChanges,
      this.organizationalUnit.valueChanges.pipe(startWith(this.organizationalUnit.value)),
      this.jobTitle.valueChanges.pipe(startWith(this.jobTitle.value)),
    ])
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.emitFormData('jobTitle');
      });

    combineLatest([this.hasChangesWorkingTimeGroup.valueChanges, this.workingTimeGroupId.valueChanges.pipe(startWith(this.workingTimeGroupId.value))])
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.emitFormData('workingTimeGroup');
      });
  }

  private async emitFormData(sectionKey: string): Promise<void> {
    let emptyData = this.createEmptyAnnexChangedDataStepModel();

    if (
      !this.hasChangesJobTitle.value &&
      !this.hasChangesWages.value &&
      !this.hasChangesTemporaryWorkplace.value &&
      !this.hasChangesPotentialSupervisor.value &&
      !this.hasChangesWorkingTimeGroup.value
    ) {
      this.startLoading(sectionKey);
      this.dataChange.emit({ data: emptyData, sectionKey: sectionKey });
      return;
    }

    if (!this.isFormValid) {
      return;
    }
    this.startLoading(sectionKey);
    let data = emptyData;

    if (this.hasChangesJobTitle.value) {
      data.ChangesJobTitle = true;
      data.OrganizationalUnitName = this.organizationalUnit.value ? this.organizationalUnit.value.Name : null;
      data.JobTitleName = this.currentJobTitle ? this.currentJobTitle.Name : null;
      data.JobTitleId = this.currentJobTitle ? this.currentJobTitle.Id : null;
      data.OrganizationalUnitId = this.organizationalUnit.value ? this.organizationalUnit.value.Id : null;
    }

    if (this.hasChangesPotentialSupervisor.value) {
      data.ChangesSupervisor = true;
      if (typeof this.potentialSupervisor.value === 'object') {
        data.SupervisorName = this.potentialSupervisor.value.Name;
        data.PotentialSupervisorId = this.potentialSupervisor.value.Id;
      }
    }

    if (this.hasChangesTemporaryWorkplace.value) {
      data.ChangesTemporaryWorkplace = true;

      let temporaryWorkplaceName: string | null = null;
      if (!this.temporaryWorkplace.value) {
        temporaryWorkplaceName = null;
      } else {
        if (typeof this.temporaryWorkplace.value === 'object') {
          temporaryWorkplaceName = this.temporaryWorkplace.value.Name;
          data.TemporaryWorkplaceId = this.temporaryWorkplace.value.Id;
        } else {
          temporaryWorkplaceName = this.temporaryWorkplace.value;
        }
      }

      data.TemporaryWorkplaceName = temporaryWorkplaceName;
    }

    if (this.hasChangesWages.value) {
      data.ChangesWage = true;

      const wageTypes = await firstValueFrom(this.listOfWageTypes$);
      const currencies = await firstValueFrom(this.listOfCurrencies$);
      data.Wages = this.wages.getRawValue().map((wage) => {
        const wageType = wageTypes.find((wt) => wt.Id === wage.wageTypeId);
        const currency = currencies.find((c) => c.Id === wage.currencyId);

        return {
          Value: wage.value,
          CurrencyName: currency?.Name || null,
          WageTypeName: wageType?.Name || null,
          CalculationDescription: wage.CalculationDescription,
          CurrencyId: wage.currencyId,
          WageTypeId: wage.wageTypeId,
          WageDescriptionId: wage.wageDescriptionId,
        };
      });
    }

    if (this.hasChangesWorkingTimeGroup.value) {
      data.ChangesWorkingTimeGroup = true;
      data.WorkingTimeGroupId = this.workingTimeGroupId.value;
      const workingTimeGroups = await firstValueFrom(this.listOfWorkingTimeGroups$);
      const wg = workingTimeGroups.find((w) => w.Id === this.workingTimeGroupId.value);
      data.WorkingTimeGroupName = wg?.Name ?? null;
    }

    this.dataChange.emit({ data: data, sectionKey });
  }

  startLoading(sectionKey: string): void {
    this.loadingStates[sectionKey] = { isLoading: true, showSuccess: false };
  }

  showSuccess(sectionKey: string): void {
    this.loadingStates[sectionKey] = { isLoading: false, showSuccess: true };

    timer(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadingStates[sectionKey] = { isLoading: false, showSuccess: false };
      });
  }

  showError(sectionKey: string): void {
    this.loadingStates[sectionKey] = { isLoading: false, showSuccess: false };
  }

  private createDialogConfig(): MatDialogConfig<TreeViewerComponentData> {
    return {
      disableClose: false,
      autoFocus: false,
      panelClass: 'standard-dialog',
      data: {
        header: Messages.ChooseOrganizationalUnit,
        emptyTreeMessage: Messages.EmptyOrganizationalUnitTree,
        datasource$: this.listOfOrganizationalUnits$,
      },
    };
  }

  public isEmploymentAgreementType(): boolean {
    return this.employmentTypeId === EmploymentType.EmploymentAgreement || this.employmentTypeId === EmploymentType.TemporaryEmploymentAgreement;
  }

  private createEmptyAnnexChangedDataStepModel(): AnnexChangedDataStepModel {
    return {
      ChangesJobTitle: false,
      JobTitleName: null,
      JobTitleId: null,
      OrganizationalUnitName: null,
      ChangesWage: false,
      Wages: [],
      ChangesTemporaryWorkplace: false,
      TemporaryWorkplaceName: null,
      ChangesSupervisor: false,
      SupervisorName: null,
      OrganizationalUnitId: null,
      PotentialSupervisorId: null,
      TemporaryWorkplaceId: null,
      ChangesWorkingTimeGroup: false,
      WorkingTimeGroupId: null,
      WorkingTimeGroupName: null,
    };
  }

  async onOrganizationalUnitClick(): Promise<void> {
    const res = await firstValueFrom(this.dialog.open(TreeViewerComponent, this.createDialogConfig()).afterClosed().pipe(first()));

    if (res) {
      this.organizationalUnit.setValue(<DictionaryItem>{ Id: res.id, Name: res.name });
    }
  }

  displayValue = (item: TreeNode | DictionaryItem): string => {
    return item ? item.Name : '';
  };
}
